/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Ring2All Team
 📄 File: SecurityLogGrid.tsx
 🗓️ Created: 2025-12-02
 🧩 Description:
    Data grid específico para Security Logs con filtros integrados
────────────────────────────────────────────────────────────────*/

import { Shield } from 'lucide-react';
import DataGridSimple from '../../../components/ui/DataGridSimple';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import DateRangePicker, { DateRange } from '../../../components/ui/DateRangePicker';
import SimpleTooltip from '../../../components/ui/SimpleTooltip';
import { useI18n } from '../../../hooks/useI18n';
import { SecurityLog, SecurityLogFilterValues } from '../types/securityLog.types';
import { SecurityActionMultiSelect } from './SecurityActionMultiSelect';
import { useUsersList } from '../hooks/useUsersList';

interface GridColumn {
  key: string;
  label: string;
  visible: boolean;
  sortable?: boolean;
  render?: (value: unknown, item: SecurityLog) => React.ReactNode;
}

interface GridPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface SecurityLogGridProps {
  data: SecurityLog[];
  columns: GridColumn[];
  loading: boolean;
  pagination: GridPagination;
  searchQuery: string;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  filters: SecurityLogFilterValues;
  onSearch: (query: string) => void;
  onSort: (column: string) => void;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (limit: number) => void;
  onColumnToggle: (key: string) => void;
  onFilterChange: (filters: SecurityLogFilterValues) => void;
}

export function SecurityLogGrid({
  data,
  columns,
  loading,
  pagination,
  searchQuery,
  sortBy,
  sortOrder,
  filters,
  onSearch,
  onSort,
  onPageChange,
  onItemsPerPageChange,
  onColumnToggle,
  onFilterChange,
}: SecurityLogGridProps) {
  const { t } = useI18n();
  const { users, loading: usersLoading } = useUsersList();

  // Build DateRange value from filters
  const dateRangeValue: DateRange = {
    startDate: filters.startDate ? filters.startDate.split('T')[0] : new Date().toISOString().split('T')[0],
    endDate:   filters.endDate   ? filters.endDate.split('T')[0]   : new Date().toISOString().split('T')[0],
    startTime: filters.startDate && filters.startDate.includes('T') ? filters.startDate.split('T')[1].slice(0, 5) : '00:00',
    endTime:   filters.endDate   && filters.endDate.includes('T')   ? filters.endDate.split('T')[1].slice(0, 5)   : '23:59',
  };

  return (
    <div className="security-logs-wrapper">
      {/* Filters Card - PRIMERO */}
      <div className="pt-16">
        <div className="p-3 bg-card/75 backdrop-blur-md border border-border/50 rounded-lg shadow-sm">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">

            {/* Row 1: Date Range | Actions */}
            <Label className="text-left">
              {t('securityLogs.filters.dateRange') || 'Date Range'}
            </Label>
            <DateRangePicker
              value={dateRangeValue}
              onChange={(range: DateRange) => onFilterChange({
                ...filters,
                startDate: `${range.startDate}T${range.startTime || '00:00'}`,
                endDate:   `${range.endDate}T${range.endTime   || '23:59'}`,
              })}
            />
            <SimpleTooltip
              content={t('securityLogs.filters.action.tooltip')}
              title={t('securityLogs.filters.action.label')}
              position="top"
              maxWidth="300px"
            >
              <Label className="text-left">{t('securityLogs.filters.action.label')}</Label>
            </SimpleTooltip>
            <SecurityActionMultiSelect
              selectedActions={filters.actions}
              onChange={(actions) => onFilterChange({ ...filters, actions })}
              placeholder={t('securityLogs.filters.action.all')}
            />

            {/* Row 2: User | Resource */}
            <SimpleTooltip
              content={t('securityLogs.filters.user.tooltip')}
              title={t('securityLogs.filters.user.label')}
              position="top"
              maxWidth="300px"
            >
              <Label className="text-left">{t('securityLogs.filters.user.label')}</Label>
            </SimpleTooltip>
            <select
              value={filters.userId}
              onChange={(e) => onFilterChange({ ...filters, userId: e.target.value })}
              className="rounded-md border border-border bg-background px-3 py-1.5 text-sm h-9"
              disabled={usersLoading}
            >
              <option value="">{usersLoading ? 'Loading...' : t('securityLogs.filters.user.all')}</option>
              {users.map((user) => (
                <option key={user.id} value={user.id.toString()}>
                  {user.fullName ? `${user.fullName} (${user.username})` : user.username}
                </option>
              ))}
            </select>

            <SimpleTooltip
              content={t('securityLogs.filters.resource.tooltip')}
              title={t('securityLogs.filters.resource.label')}
              position="top"
              maxWidth="300px"
            >
              <Label className="text-left">{t('securityLogs.filters.resource.label')}</Label>
            </SimpleTooltip>
            <Input
              type="text"
              value={filters.resource}
              onChange={(e) => onFilterChange({ ...filters, resource: e.target.value })}
              placeholder={t('securityLogs.filters.resource.placeholder')}
              className="h-9"
            />
          </div>
        </div>
      </div>

      {/* Data Grid - DESPUÉS */}
      <DataGridSimple
        data={data}
        columns={columns}
        loading={loading}
        pagination={pagination}
        searchQuery={searchQuery}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSearch={onSearch}
        onSort={onSort}
        onPageChange={onPageChange}
        onItemsPerPageChange={onItemsPerPageChange}
        onColumnToggle={onColumnToggle}
        title={t('securityLogs.title')}
        titleIcon={<Shield className="h-5 w-5 text-primary" />}
        searchPlaceholder={t('securityLogs.searchPlaceholder')}
        emptyMessage={t('securityLogs.emptyMessage')}
      />
    </div>
  );
}

// Global CSS to remove spacing between filter box and grid
if (typeof document !== 'undefined') {
  const styleId = 'security-logs-spacing-fix';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      .security-logs-wrapper {
        display: flex;
        flex-direction: column;
        gap: 0;
      }
      
      .security-logs-wrapper .space-y-6 > :not([hidden]) ~ :not([hidden]) {
        margin-top: 0 !important;
      }
    `;
    document.head.appendChild(style);
  }
}
